import { QueryKey, UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useState } from 'react';
import { useUnmount } from 'react-use';

import { ReqDatetime, ReqPagination, ResPagination } from '@flipster/apis/types';
import { dateToEpochString } from '@flipster/utils/datetime';

interface TableReq extends ReqPagination, ReqDatetime {
  symbol?: string | undefined;
}

export function useStepPagination<
  Params extends TableReq,
  Res extends ResPagination,
  DataKey extends keyof Res,
  TQueryKey extends QueryKey = QueryKey,
>(
  dataKey: DataKey,
  queryKey: TQueryKey,
  queryFn: (params: Params) => Promise<AxiosResponse<Res>>,
  {
    passParams,
    count = 25,
    ...options
  }: Omit<UseQueryOptions<unknown, AxiosError<unknown>, AxiosResponse<Res>>, 'queryKey' | 'queryFn'> & {
    passParams?: Partial<Params>;
    count?: number;
  },
) {
  // 바뀌지 않는 params 는 useRef 로 관리
  const [params, setParams] = useState<Partial<Params>>(passParams ?? {});

  const mergeParams = {
    ...passParams,
    ...params,
    count,
  } as Params;

  const queryClient = useQueryClient();

  const { isInitialLoading, isFetching, data, ...restQuery } = useQuery<
    unknown,
    AxiosError<unknown>,
    AxiosResponse<Res>
  >({
    queryKey: [...queryKey, mergeParams],
    queryFn: () => queryFn(mergeParams),
    ...options,
  });

  // 필터 설정시 pagination 초기화
  const setFilter = useCallback((next?: Partial<Params>) => {
    setParams((prevState) => ({ ...prevState, after: undefined, before: undefined, ...next }));
  }, []);

  const setSymbolFilter = useCallback(
    (symbol: string | undefined) => {
      setFilter({ symbol } as Partial<Params>);
    },
    [setFilter],
  );

  const setDateFilter = useCallback(
    (date: Date | undefined) => {
      setFilter({ startTime: date ? dateToEpochString(date) : null } as Partial<Params>);
    },
    [setFilter],
  );

  const setRangeFilter = useCallback(
    ([startTime, endTime]: [Date | undefined, Date | undefined]) => {
      setFilter({
        startTime: startTime ? dateToEpochString(startTime) : null,
        endTime: endTime ? dateToEpochString(endTime) : null,
      } as Partial<Params>);
    },
    [setFilter],
  );

  const setPagination = useCallback(({ before, after }: { before?: string; after?: string }) => {
    setParams((prevState) => ({ ...prevState, before, after }));
  }, []);

  const resetParams = useCallback(() => {
    setParams(passParams ?? {});
  }, [passParams]);

  useUnmount(() => {
    queryClient.removeQueries({ queryKey });
  });

  return {
    ...restQuery,
    isLoading: isInitialLoading || isFetching,
    hasPrev: !!data?.data?.hasPrev,
    hasNext: !!data?.data?.hasNext,
    data: data?.data?.[dataKey],
    params,
    setFilter,
    setSymbolFilter,
    setDateFilter,
    setRangeFilter,
    setPagination,
    resetParams,
  };
}
